import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { retryAfterSeconds } from '../utils/retry-after';

/**
 * Session policy (C1, 2026-10-01) — the pure rules behind this tab's sign-in: timings, storage keys, cross-tab
 * message shapes, token decoding, the start-up decision, renewal-error classification and the sign-in page texts.
 *
 * No Angular DI and no side effects except saveReason() (one sessionStorage write), so every rule is unit-tested on
 * its own (session-policy.spec.ts). AuthService runs the state machine on top of these.
 */

// ── TIMINGS ─────────────────────────────────────────────────────────
export const IDLE_TIMEOUT_MS = 15 * 60_000;          // 15 min without real input → signed out (server mirrors it)
export const WARNING_MS = 60_000;                    // the 60…1 countdown before the idle sign-out (TC-0066)
export const RESTORE_GAP_MS = 30_000;                // a tab reloaded/reopened within 30 s resumes without a password
export const HEARTBEAT_MS = 10_000;                  // tab-seen heartbeat + idle watchdog (covers sleep, TC-0069)
export const REFRESH_LEAD_MS = 5 * 60_000;           // renew 5 min before the local expiry
export const MIN_REFRESH_DELAY_MS = 10_000;
export const REFRESH_JITTER_MS = 20_000;             // spreads duplicated tabs that share one JWT exp
export const TRANSIENT_RETRY_MS = 30_000;            // network/server failure: retry after 30 s (or on 'online')
export const MAX_429_RETRY_MS = 60_000;              // R1: a renewal 429 waits its Retry-After, at most 60 s
export const REFRESH_TIMEOUT_MS = 20_000;
export const ACTIVITY_THROTTLE_MS = 5_000;
export const HOUSEKEEP_AFTER_MS = 24 * 3_600_000;    // mt_act_* / mt_signout_* older than 24 h are purged
/** Body mode: how long a tab that may have missed a rotation waits for a live tab of its session to hand over the
 *  current token pair before it renews with its own (a rotated-away refresh token would trip S1 reuse detection). */
export const PEER_SYNC_WAIT_MS = 400;
/** A renewal timer firing this much later than planned means the tab was frozen or suspended meanwhile. */
export const SUSPENDED_GAP_MS = 30_000;

/** requires L1 in the same build (LockStateService, lockedRouteGuard on /change-password and /tele, inert background,
 *  idle modal hidden while locked). Set false if L1 is not merged: resume-locked then becomes end-stale. */
export const RESUME_LOCKED_ENABLED = true;

// ── STORAGE KEYS AND CHANNELS ───────────────────────────────────────
export const AUTH_KEY = 'meditrack_user';
export const OWNER_STASH_KEY = 'meditrack_owner_stash';
export const LOCK_KEY = 'meditrack_locked';           // = LockStateService.STORAGE_KEY
export const TAB_SEEN_KEY = 'mt_tab_seen_at';         // sessionStorage, ms
export const LOGOUT_REASON_KEY = 'mt_logout_reason';  // = AuthService.SIGN_OUT_REASON_KEY, read once by LoginComponent
/** localStorage: ms of the last real input by that person in any tab of this browser. */
export const activityKey = (staffId: number | string): string => 'mt_act_' + staffId;
/** localStorage: JSON {at, reason} of that person's last user-scope sign-out in this browser. */
export const signedOutKey = (staffId: number | string): string => 'mt_signout_' + staffId;
export const CHANNEL = 'mt-auth';
export const sessionChannelName = (sid: string): string => 'mt-auth:' + sid;
export const LEGACY_LOGOUT = 'logout';

// ── HTTP MARKERS ────────────────────────────────────────────────────
/** The support-session renewal sent with the OWNER's own token: AuthService decides what its refusals mean, so
 *  SessionTimeoutInterceptor passes them through instead of signing out on its own. */
export const OWNER_AUTH_REQUEST = new HttpContextToken<boolean>(() => false);
/** Request header that makes the Angular service worker leave a request alone (DISC-012, owner decision 8).
 *  ngsw-config.json has NO dataGroups, and AuthService deletes every service-worker data store (':data:') at each page
 *  load and sign-out, so ngsw-config.json must have no dataGroups at all. AuthInterceptor sets this header on every API
 *  request. The interceptor-free calls (renewal, server logout, lock-screen unlock, journey) set it themselves, and any
 *  NEW fetch() or HttpClient(HttpBackend) call to the API must set it too. So the worker never answers or stores an API
 *  reply, and an offline failure stays a real network error (status 0) that the offline write queue recognises.
 *  /auth/me and /auth/sessions also set it explicitly. Depends on the API CORS policy allowing this header
 *  (MediTrackCors: AllowAnyHeader). */
export const NGSW_BYPASS = { 'ngsw-bypass': 'true' } as const;
/** DISC-012 / TC-5494: localStorage copies of the hospital's settings and plan kept for a fast first paint
 *  (LayoutComponent.loadHospitalBranding and the subscription load; SettingsComponent also writes 'hospitalBranding').
 *  They are not tied to a person or hospital, so AuthService removes them at every sign-out. */
export const SHARED_PC_UI_CACHE_KEYS = ['hospitalBranding', 'hospitalSubscription'] as const;
/** The saved copy for one hospital (TC-5571): two tabs can be signed in to two hospitals. */
export function uiCacheKey(base: typeof SHARED_PC_UI_CACHE_KEYS[number], hospitalId: number | string | null | undefined): string {
  return `${base}:${hospitalId ?? 0}`;
}

// ── URL RULES ───────────────────────────────────────────────────────
/** a 401 from these is a business answer (wrong password/code), never a lost session */
export const PASS_THROUGH_401 = /\/auth\/(login|verify-mfa|resend-mfa|mfa-setup\/(verify|email-code)|refresh|forgot-password|set-password|verify-password)\b/i;
/** Writes that never show the 'Save failed — no network' toast (also matches logout-all). */
export const NO_NET_TOAST_URL = /\/auth\/(logout|refresh)\b/i;

// ── TEXTS (P1 wording) ──────────────────────────────────────────────
export const IDLE_TEXT = 'You were signed out after 15 minutes without activity. Please sign in again.';
export const ENDED_TEXT = 'Your session was ended. Please sign in again.';
export const DEACTIVATED_TEXT = 'Your account has been deactivated. Please contact your administrator.';
export const SUSPENDED_FALLBACK = "Your hospital's MediTrack access is suspended. Please contact MediTrack support.";
export const LOCKED_OUT_FALLBACK = 'Too many wrong passwords. For your safety you have been signed out. Please sign in again.';
export const OTHER_TAB_TEXT = 'You signed out in another tab.';
export const EVERYWHERE_TEXT = 'You signed out on all devices.';
export const SUPPORT_ENDED_TEXT = 'Your support session has ended — the hospital ended it or the time ran out.';
export const S1_GENERIC_ENDED = 'Session has been ended. Please log in again.';
export const SESSION_REVOKED_TEXT = 'You were signed out because this sign-in was ended (by an administrator, from another device, or with "Sign out everywhere"). Please sign in again.';
export const REFRESH_EXPIRED_TEXT = 'Your sign-in has expired. Please sign in again.';
/** TC-0228: the server ended this sign-in for safety — an old or invalid renewal was presented for it (REFRESH_REUSED,
 *  or TOKEN_REVOKED with reason RefreshReuse / TokenMismatch). Says why, not just "Your session was ended." */
export const REFRESH_SAFETY_TEXT = 'You were signed out for safety because an old or invalid sign-in was used for your account. Please sign in again. If this keeps happening, tell your administrator.';
export const SAVE_FAILED_TEXT = 'Save failed — no network connection. Please check your Wi-Fi and try again.';
/** TC-0126: the server code (login / verify-mfa 403, refresh 401) and the web's own ending for a role that is not a
 *  hospital role or 'SuperAdmin' (an old invite bug or a hand-edited row). */
export const ACCOUNT_SETUP_INVALID = 'ACCOUNT_SETUP_INVALID';
export const ACCOUNT_SETUP_INVALID_TEXT = "This account isn't set up correctly. Contact your administrator.";

/** Sign-in page text per server code (contract section O). Codes not listed show ENDED_TEXT. */
export const SIGN_OUT_TEXT = new Map<string, string>([
  ['ACCOUNT_DEACTIVATED', DEACTIVATED_TEXT],
  ['ACCOUNT_DISABLED', DEACTIVATED_TEXT],
  ['TOKEN_REVOKED', ENDED_TEXT],
  ['REFRESH_REUSED', REFRESH_SAFETY_TEXT],      // TC-0228
  ['SESSION_REVOKED', SESSION_REVOKED_TEXT],
  ['SESSION_EXPIRED', IDLE_TEXT],
  ['IDLE_TIMEOUT', IDLE_TEXT],
  ['REFRESH_EXPIRED', REFRESH_EXPIRED_TEXT],
  ['REFRESH_INVALID', REFRESH_EXPIRED_TEXT],    // TC-0228: renewal refused (old access token + unknown refresh), session kept
]);

/** TOKEN_REVOKED carries the server's revoke `reason`; these reasons pick their own text (any other: ENDED_TEXT). */
export const TOKEN_REVOKED_REASON_TEXT = new Map<string, string>([
  ['RefreshReuse', REFRESH_SAFETY_TEXT],        // TC-0228
  ['TokenMismatch', REFRESH_SAFETY_TEXT],
  ['Replaced', ENDED_TEXT],                     // DISC-030: an access token a renewal replaced
]);

// ── ROLES ───────────────────────────────────────────────────────────
/** Each sign-in role's home page, spelled exactly as the server issues it. Every home route admits its own role
 *  (auth.guard.spec checks this against app.routes), so AuthGuard's wrong-role redirect can never reach itself. */
export const ROLE_HOME: Readonly<Record<string, string>> = {
  SuperAdmin:    '/super-admin',
  HospitalAdmin: '/dashboard',
  Doctor:        '/doctor-dashboard',
  Receptionist:  '/receptionist-dashboard',
  Lab:           '/lab-dashboard',
  Pharmacy:      '/pharmacy-dashboard',
  Nurse:         '/nurse-dashboard',
  Radiology:     '/lab-dashboard',   // Radiology retired — route to Lab
  Ambulance:     '/ambulance-dashboard',
  Billing:       '/billing-counter',
};

/** TC-0126: a role with no home page here ('Admin', 'superadmin ', '') is never sent to a page that would refuse it
 *  and bounce it back (that loop froze the tab); its session is ended instead. */
export function isKnownRole(role: string | null | undefined): boolean {
  return typeof role === 'string' && Object.prototype.hasOwnProperty.call(ROLE_HOME, role);
}

// ── TYPES ───────────────────────────────────────────────────────────
export type StartupDecision = 'end-signed-out' | 'end-idle' | 'end-stale' | 'resume' | 'resume-locked';
/** Who a token belongs to: the person, the device session (sid) and, for owner support tokens, the grant. */
export type Identity = { staffId: string; sid: string | null; grant: string | null };
/** The part of BroadcastChannel AuthService uses — specs inject a fake (AUTH_CHANNEL_FACTORY). */
export interface ChannelLike { postMessage(m: unknown): void; close(): void; onmessage: ((e: MessageEvent) => void) | null }
export type UserLogoutMsg = { v: 2; type: 'logout'; scope: 'user'; staffId: number; reason: string; message: string | null };
/** 'sync': a tab that may hold a rotated-away pair (reopened, discarded, restored, unfrozen) asks the live tabs of the
 *  session for theirs; each answers with a normal 'rotated'. expLocalMs: the sender's local expiry (same device clock),
 *  so an answer about an older token is not taken as a fresh full lifetime. */
export type SessionMsg =
  | { v: 2; type: 'logout'; scope: 'session'; reason: string; code?: string; message: string | null }
  | { v: 2; type: 'rotated'; token: string; expiry: string; refreshToken?: string; expLocalMs?: number }
  | { v: 2; type: 'sync' };
export type SignOutReason = { text: string; specific: boolean };

// ── TOKEN HELPERS (never verify a signature — the server does; these only read claims) ──
export function jwtPayload(token: string | null | undefined): any | null {
  try {
    const parts = String(token ?? '').split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const p = JSON.parse(atob(b64));
    return p && typeof p === 'object' ? p : null;
  } catch { return null; }
}

export function jwtExpMs(token: string | null | undefined): number | null {
  try {
    const exp = Number(jwtPayload(token)?.exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch { return null; }
}

export function jwtSid(token: string | null | undefined): string | null {
  try {
    const sid = jwtPayload(token)?.sid;
    return typeof sid === 'string' && sid.length > 0 ? sid : null;
  } catch { return null; }
}

export function tokenIdentity(token: string | null | undefined): Identity | null {
  try {
    const p = jwtPayload(token);
    if (!p || p.staffId == null || p.staffId === '') return null;
    return {
      staffId: String(p.staffId),
      sid: p.sid ? String(p.sid) : null,
      grant: p.support_grant != null ? String(p.support_grant) : null,
    };
  } catch { return null; }
}

export function sameIdentity(a: Identity | null | undefined, b: Identity | null | undefined): boolean {
  return !!a && !!b && a.staffId === b.staffId && a.sid === b.sid && a.grant === b.grant;
}

/** TC-0159 + clock skew: never compare the server exp with the device clock */
export function localExpiryMs(token: string | null | undefined, nowMs: number): number | null {
  try {
    const p = jwtPayload(token);
    const exp = Number(p?.exp), iat = Number(p?.iat);
    if (p && p.exp != null && p.iat != null && Number.isFinite(exp) && Number.isFinite(iat) && exp > iat) {
      return nowMs + (exp - iat) * 1000;
    }
    return jwtExpMs(token);
  } catch { return null; }
}

export function tokenExpMs(u: { token: string; expiry?: string; expLocalMs?: number | null }): number {
  try {
    return u.expLocalMs ?? jwtExpMs(u.token) ?? (Date.parse(u.expiry ?? '') || 0);
  } catch { return 0; }
}

export function readNum(storage: Storage | null | undefined, key: string): number | null {
  try {
    const raw = storage?.getItem(key);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

/** Parses an mt_signout_<id> value: JSON {at, reason}; a bare number (older format) counts as reason 'user'. */
export function parseSignedOut(raw: string | null | undefined): { at: number; reason: string } | null {
  try {
    if (raw == null || raw === '') return null;
    const v = JSON.parse(raw);
    if (typeof v === 'number') return Number.isFinite(v) ? { at: v, reason: 'user' } : null;
    if (v && typeof v === 'object' && typeof v.at === 'number' && Number.isFinite(v.at)) {
      return { at: v.at, reason: typeof v.reason === 'string' ? v.reason : 'user' };
    }
    return null;
  } catch { return null; }
}

export function readSignedOut(storage: Storage | null | undefined, staffId: number | string): { at: number; reason: string } | null {
  try { return parseSignedOut(storage?.getItem(signedOutKey(staffId))); } catch { return null; }
}

// ── START-UP ────────────────────────────────────────────────────────
export interface StartupInput {
  nowMs: number;
  tabSeenAtMs: number | null;
  sharedActivityMs: number | null;
  signedOutAtMs: number | null;
  signedInAtMs: number | null;
  tokenExpMs: number;
  discarded: boolean;
}

/** End, resume, or resume behind the lock screen (TC-0004). `resumeLocked` exists so specs can test the switch-off. */
export function decideStartup(i: StartupInput, resumeLocked: boolean = RESUME_LOCKED_ENABLED): StartupDecision {
  // (1) the person signed out in this browser after this tab signed in
  if (i.signedOutAtMs != null && i.signedInAtMs != null && i.signedInAtMs <= i.signedOutAtMs) return 'end-signed-out';
  // (2) idle for 15 min or more in every tab of this browser
  if (i.sharedActivityMs != null && i.nowMs - i.sharedActivityMs >= IDLE_TIMEOUT_MS) return 'end-idle';
  // (3) never resume + renew when presence cannot be shown
  if (i.sharedActivityMs == null && i.tokenExpMs <= i.nowMs) return 'end-stale';
  // (4) the browser discarded the tab, or the person / this tab was here moments ago (F5, support reload)
  if (i.discarded
      || (i.sharedActivityMs != null && i.nowMs - i.sharedActivityMs <= RESTORE_GAP_MS)
      || (i.tabSeenAtMs != null && i.nowMs - i.tabSeenAtMs <= RESTORE_GAP_MS)) return 'resume';
  // (5) a closed tab reopened later: ask for the password
  return resumeLocked ? 'resume-locked' : 'end-stale';
}

// ── CROSS-TAB ───────────────────────────────────────────────────────
/** Only a v2 user-scope logout for THIS person is honoured; the legacy string, other people and session-scope
 *  messages are ignored (DISC-003). */
export function shouldHonourUserLogout(msg: unknown, myStaffId: number | string): boolean {
  try {
    const m = msg as Partial<UserLogoutMsg> | null;
    return !!m && typeof m === 'object' && m.v === 2 && m.type === 'logout' && m.scope === 'user'
      && String(m.staffId) === String(myStaffId);
  } catch { return false; }
}

// ── RENEWAL ERRORS ──────────────────────────────────────────────────
/** Any refused renewal (4xx) is terminal; network/server trouble (0, 408, 429, 5xx, timeout) is transient. */
export function classifyRefreshError(err: unknown): { terminal: boolean; status: number; code?: string; message?: string; reason?: string } {
  try {
    if ((err as any)?.message === 'owner sign-in expired') return { terminal: true, status: 401, code: 'OWNER_SIGNIN_EXPIRED' };
    if (err instanceof HttpErrorResponse) {
      const s = err.status;
      if (s === 0 || s === 408 || s === 429 || s >= 500) return { terminal: false, status: s };
      if (s >= 400) {
        const body = err.error && typeof err.error === 'object' ? err.error : null;
        return {
          terminal: true, status: s,
          code: typeof body?.code === 'string' ? body.code : undefined,
          message: typeof body?.message === 'string' ? body.message : undefined,
          // TC-0228: TOKEN_REVOKED's revoke reason (RefreshReuse, TokenMismatch, Replaced…) picks the sign-in page text
          ...(typeof body?.reason === 'string' ? { reason: body.reason } : {}),
        };
      }
      return { terminal: false, status: s };
    }
  } catch { /* fall through */ }
  return { terminal: false, status: 0 };   // rxjs TimeoutError, TypeError, …
}

/** How long a transient renewal failure waits before the next try. R1 (TC-0009): a 429 waits the server's
 *  Retry-After (the refresh bucket says 10 s), kept within 1-60 s; anything else, or a 429 without a wait, 30 s. */
export function transientRetryMs(err: unknown): number {
  try {
    if (err instanceof HttpErrorResponse && err.status === 429) {
      const s = retryAfterSeconds(err);
      if (s != null) return Math.min(Math.max(1, s) * 1000, MAX_429_RETRY_MS);
    }
  } catch { /* fall through */ }
  return TRANSIENT_RETRY_MS;
}

// ── SIGN-IN PAGE REASON ─────────────────────────────────────────────
export function reasonFor(
  kind: string,
  o: { code?: string; message?: string; remoteReason?: string; reason?: string },
): SignOutReason | null {
  try {
    const code = o.code, message = o.message;
    switch (kind) {
      case 'user':
      case 'everywhere':
        return null;
      case 'idle':
        return { text: IDLE_TEXT, specific: false };
      case 'stale':
        return { text: message ?? ENDED_TEXT, specific: false };
      case 'remote': {
        const text = o.remoteReason === 'idle' ? IDLE_TEXT
          : o.remoteReason === 'everywhere' ? EVERYWHERE_TEXT
          : o.remoteReason === 'user' ? OTHER_TAB_TEXT
          : (message ?? ENDED_TEXT);
        return { text, specific: false };
      }
      case 'forced':
        if (code === 'HOSPITAL_SUSPENDED' || (!code && /suspended/i.test(message ?? ''))) return { text: message || SUSPENDED_FALLBACK, specific: true };
        if (code === 'UNLOCK_LOCKED_OUT') return { text: message || LOCKED_OUT_FALLBACK, specific: true };
        if (code === 'ACCOUNT_DEACTIVATED' || code === 'ACCOUNT_DISABLED') return { text: DEACTIVATED_TEXT, specific: true };
        if (code === ACCOUNT_SETUP_INVALID) return { text: ACCOUNT_SETUP_INVALID_TEXT, specific: true };
        // S1's password changed / reset texts arrive as SESSION_REVOKED with their own message.
        if (code === 'SESSION_REVOKED' && message && message !== S1_GENERIC_ENDED) return { text: message, specific: true };
        // TC-0228 / DISC-030: TOKEN_REVOKED says why when its reason is known (ended for safety, token replaced).
        if (code === 'TOKEN_REVOKED' && o.reason && TOKEN_REVOKED_REASON_TEXT.has(o.reason)) {
          return { text: TOKEN_REVOKED_REASON_TEXT.get(o.reason)!, specific: false };
        }
        if (code && SIGN_OUT_TEXT.has(code)) return { text: SIGN_OUT_TEXT.get(code)!, specific: false };
        return { text: ENDED_TEXT, specific: false };
      default:
        return null;
    }
  } catch { return null; }
}

// ── SERVER LOGOUT REASON ────────────────────────────────────────────
/** TC-0114: POST /auth/logout {reason} — the API writes it on the hospital's SIGNED_OUT audit row. */
export type LogoutReason = 'manual' | 'idle' | 'otherTab' | 'expired';

/** The person pressed Logout → 'manual'; the 15-min idle rule → 'idle'; a sign-out in another tab of this browser
 *  (live, or found at start-up) → 'otherTab'; the session was already over (refused renewal, server refusal, a stale
 *  tab, a broken account) → 'expired'. 'everywhere' sends no logout (logout-all already ended the sessions). */
export function logoutReasonFor(kind: string): LogoutReason {
  switch (kind) {
    case 'idle':   return 'idle';
    case 'remote': return 'otherTab';
    case 'forced':
    case 'stale':  return 'expired';
    default:       return 'manual';
  }
}

/** P1's rule: specific reasons always win; a generic one never overwrites a reason already saved. */
export function saveReason(r: SignOutReason): void {
  try {
    if (!r.specific && sessionStorage.getItem(LOGOUT_REASON_KEY)) return;
    sessionStorage.setItem(LOGOUT_REASON_KEY, r.text);
  } catch { /* storage unavailable */ }
}
